import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { GetUsers } from "api/users.api";
import { MAX_ROWS_FETCH, NUMBER_OF_ROWS_PER_PAGE } from "config/constants";
import { min } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { User, usersListResponse } from "types/userInfo";
import { KeyValuePair } from "utils/common";


export class UserListStore {
  root: RootStore;
  loading = true;
  error: ErrorResponseDto | null = null

  usersList: User[] = [];
  //this is for loading 200 records and FE paginating when the sort order in based on unpaginated sort fields
  catchedUserList: User[] = [];
  totalCount: number = 0;
  nextToken: string = ""
  searchTerm: string = "";
  searchCategory: string = "";
  searchCategoriesList: KeyValuePair[] = [
    { value: 'name_filter', key: 'Name' },
    { value: 'location_name_filter', key: 'Location' },
    { value: 'is_outdoors', key: 'Location' },
  ]
  sortField: string = "last_update"
  sortDirection: "asc" | "desc" = "desc"

  constructor(root: RootStore) {
    this.root = root;
    makeObservable(this, {
      loading: observable,
      error: observable,
      totalCount: observable,
      usersList: observable,
      searchTerm: observable,
      searchCategory: observable,
      searchCategoriesList: observable,
      sortField: observable,
      sortDirection: observable,

      loadUsers: action.bound,
      setSearchTerm: action.bound,
      setSearchCategory: action.bound,
      reset: action.bound,
      onSort: action.bound,

      nextCount: computed,
      getSearchCategoryTitle: computed,
      getEmptyListMsg: computed
    });

  }

  reset() {
    this.searchTerm = ""
    this.searchCategory = ''
    this.usersList = []
    this.sortField = "last_update"
    this.sortDirection = "desc"
    this.nextToken = ''
    this.loading = true
    this.error = null
  }

  setSearchTerm(search: string) {
    this.searchTerm = search
  }
  setSearchCategory(cat: string) {
    this.searchCategory = cat
  }

  get getSearchCategoryTitle() {
    return this.searchCategoriesList.find(r => r.value === this.searchCategory)?.key || ""
  }

  frontendPagination() {
    return this.sortField === "last_update" || this.sortField === "region_name"
  }

  getLoadParams(firstPage: boolean) {
    const params: Record<string, any> = {
      limit: this.frontendPagination() ? MAX_ROWS_FETCH : NUMBER_OF_ROWS_PER_PAGE
    }

    if (this.searchTerm !== "") {
      if (this.searchCategory !== "") {
        if (this.searchCategory === 'is_outdoors')
          params.outdoors_filter = 'true'
        else
          params[this.searchCategory] = this.searchTerm;
      }
      else {
        this.searchCategoriesList.forEach(c => {
          if (c.value !== "is_outdoors")
            params[c.value] = this.searchTerm
        })
      }
    }
    if (this.sortField !== "") {
      params.sort = `${this.sortDirection === "desc" ? '-' : ''}${this.sortField}`
    }
    if (!firstPage && this.nextToken !== "")
      params.next = this.nextToken
    return params
  }

  loadUsers(firstPage = false) {
    this.loading = true
    this.error = null

    if (!firstPage && this.frontendPagination()) {
      const pageNumber = this.usersList.length / NUMBER_OF_ROWS_PER_PAGE
      const start = pageNumber * NUMBER_OF_ROWS_PER_PAGE
      const end = ((pageNumber + 1) * NUMBER_OF_ROWS_PER_PAGE)
      this.usersList = [...this.usersList, ...this.catchedUserList.slice(start, end)]
      this.loading = false
      return Promise.resolve()
    }

    return GetUsers(this.getLoadParams(firstPage))
      .then((res: usersListResponse) => {
        if (firstPage) {
          if (this.frontendPagination()) {
            this.usersList = [...res.items.slice(0, NUMBER_OF_ROWS_PER_PAGE)]
            this.catchedUserList = res.items
          }
          else {
            this.usersList = [...res.items]
          }
        }
        else {
          this.usersList = [...this.usersList, ...res.items]
        }

        this.totalCount = res.total
        this.nextToken = res.next

      })
      .catch((err: ErrorResponseDto) => this.error = err)
      .finally(() => this.loading = false)
  }

  onSort(field: string) {
    this.sortDirection = this.sortField !== field
      ? "asc"
      : this.sortDirection === "asc" ? "desc" : "asc"
    this.sortField = field
    this.loadUsers(true)
  }

  get nextCount() {
    return min([this.totalCount - this.usersList.length, NUMBER_OF_ROWS_PER_PAGE]) || 0
  }

  get getEmptyListMsg() {
    if (this.searchTerm === "")
      return "No Users to display"
    else
      return `Sorry, no results were found for ${this.searchTerm}
        ${this.searchCategory !== ""
          ? ` in <b>${this.searchCategoriesList.find(c => c.value === this.searchCategory)?.key}</b>`
          : ''
        }`
  }

}
